<template>
  <div class="confirmation__details">
    <h3 class="confirmation__participants-title">
      {{ $t('landingpage.checkout-page.confirmation.ticket_guests.title') }}
    </h3>
    <p class="mb-3">
      {{
        $t('landingpage.checkout-page.confirmation.ticket_guests.description')
      }}
    </p>

    <table class="confirmation__ticket-guest-table w-full text-left">
      <thead>
        <tr>
          <th class="confirmation__ticket-guest-th">
            {{ $t('common.ticket') }}
          </th>
          <th class="confirmation__ticket-guest-th">
            {{ $t('common.guest') }}
          </th>
          <th class="confirmation__ticket-guest-th">
            {{ $t('common.action') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="guest in ticketGuests" :key="guest.id">
          <tr class="confirmation__ticket-guest-row">
            <td class="confirmation__ticket-guest-td">
              <span>{{ getTicketName(guest) }}</span>
              <span class="text-sm opacity-60 block">
                ({{ $t('guest.code') }}: {{ guest.code }})
              </span>
            </td>
            <td class="confirmation__ticket-guest-td">
              {{ getGuestDisplayName(guest) }}
            </td>
            <td class="confirmation__ticket-guest-td">
              <button
                type="button"
                class="btn btn-primary btn-sm"
                :disabled="guestStates[guest.id]?.saving"
                @click="togglePersonalize(guest)"
              >
                {{
                  $t(
                    'landingpage.checkout-page.confirmation.ticket_guests.personalize',
                  )
                }}
              </button>
            </td>
          </tr>
          <tr v-if="expandedGuestId === guest.id" :key="`form-${guest.id}`">
            <td colspan="3" class="confirmation__ticket-guest-form-cell p-4">
              <div class="checkout-form-wrapper">
                <CheckoutForm
                  :fields="mainGuestFields"
                  :backend="backend"
                  :loading="false"
                />
                <div class="flex items-center gap-3 mt-4">
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    :disabled="guestStates[guest.id]?.saving"
                    @click="saveGuest(guest)"
                  >
                    {{
                      $t(
                        'landingpage.checkout-page.confirmation.ticket_guests.save',
                      )
                    }}
                  </button>
                  <span
                    v-if="guestStates[guest.id]?.message"
                    class="text-sm"
                    :class="
                      guestStates[guest.id]?.isError
                        ? 'text-error'
                        : 'text-green-600'
                    "
                  >
                    {{ guestStates[guest.id].message }}
                  </span>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { inject, reactive, computed, ref, watch } from 'vue'
import { patchJson } from '@/LandingPage/Checkout/useCheckoutFetch'
import { useI18n } from 'vue-i18n'
import CheckoutForm from '@/Components/LandingPage/Checkout/CheckoutForm.vue'
import { useCheckoutFormStore } from '@landingpage/Registration/composables/useCheckoutFormStore'
import { useVisibilityRules } from '@landingpage/Registration/composables/useVisibilityRules'

const { t } = useI18n()

const eventId = inject('eventId')
const orderDetails = inject('orderDetails')
const activeLocale = inject('activeLocale')
const mainGuestFields = inject('mainGuestFields')
const generateGuestFields = inject('generateGuestFields')

const store = useCheckoutFormStore()
const backend = store.createAdapter()

const ticketGuests = computed(() => orderDetails.value?.ticket_guests || [])

const guestStates = reactive({})
const expandedGuestId = ref(null)

const initGuestState = (guest) => {
  if (!guestStates[guest.id]) {
    guestStates[guest.id] = {
      saving: false,
      message: '',
      isError: false,
    }
  }
}

const ensureStatesInitialized = () => {
  ticketGuests.value.forEach((guest) => initGuestState(guest))
}

watch(ticketGuests, ensureStatesInitialized, { immediate: true })

const getTicketName = (guest) => {
  const item = guest.items?.find((i) => i.product?.type === 'ticket')
  return item?.product?.name?.[activeLocale] || ''
}

const getGuestDisplayName = (guest) => {
  const firstName = guest.contact?.first_name || ''
  const lastName = guest.contact?.last_name || ''
  if (!firstName && !lastName) {
    return t(
      'landingpage.checkout-page.confirmation.ticket_guests.unpersonalized',
    )
  }
  return `${firstName} ${lastName}`.trim()
}

const togglePersonalize = (guest) => {
  if (expandedGuestId.value === guest.id) {
    expandedGuestId.value = null
    return
  }

  expandedGuestId.value = guest.id

  const initialData = generateGuestFields(guest, mainGuestFields.value?.items)
  store.initialize(initialData, mainGuestFields.value)
}

const clearMessage = (guestId) => {
  setTimeout(() => {
    if (guestStates[guestId]) {
      guestStates[guestId].message = ''
    }
  }, 5000)
}

const validateForm = () => {
  if (!mainGuestFields.value?.items) return true

  const { evaluateVisibilityRules } = useVisibilityRules(store.formData)
  const visibleFields = mainGuestFields.value.items.filter((item) => {
    if (item.hidden) return false
    if (!item.visibility_rules) return true
    return evaluateVisibilityRules(item.visibility_rules)
  })

  return store.validate(visibleFields)
}

const saveGuest = async (guest) => {
  const state = guestStates[guest.id]
  state.message = ''

  if (!validateForm()) return

  state.saving = true

  const payload = store.formData

  const { error, response } = await patchJson(
    route('api.checkout.ticket-guest.update', {
      event: eventId,
      order: orderDetails.value.id,
      guest: guest.id,
      locale: activeLocale,
    }),
    payload,
  )

  state.saving = false

  if (error.value && response.value?.status !== 204) {
    state.isError = true
    state.message = error.value?.message || 'Error'

    if (error.value?.errors) {
      store.setErrors(error.value.errors)
    }
  } else {
    state.isError = false
    state.message = t(
      'landingpage.checkout-page.confirmation.ticket_guests.saved',
    )

    // Update guest data in orderDetails so the table display refreshes
    if (payload.contact) {
      guest.contact = { ...guest.contact, ...payload.contact }
    }

    expandedGuestId.value = null
  }

  clearMessage(guest.id)
}
</script>
